import React, { useEffect, useState } from 'react';
import { useFormatTarget, formatActions } from '../lib/formatTarget';
import {
  BellIcon, CheckboxEmptyIcon, CheckboxDoneIcon, ArrowIcon,
  BoldGlyph, ItalicGlyph, StrikeGlyph, HeaderGlyph, LogGlyph,
} from './FormatMenu';

/**
 * MobileFormatBar — Walter 2026-07-02; icon refresh Walter 2026-07-16 (same
 * approved mockup as FormatMenu — icons shared from there). The desktop format
 * strip lives in the top header, but the mobile top bar + tile header have no
 * room for it, so on a phone formatting rides a slim toolbar pinned just above
 * the on-screen keyboard, visible only while a list textarea is focused.
 *
 * - Visibility keys off the global formatTarget (`focused`) — same store the
 *   desktop FormatMenu uses. Hidden when nothing's focused or the list is
 *   read-only (viewer).
 * - Position tracks the keyboard via the VisualViewport API: the bar sits at
 *   the top edge of the keyboard (bottom = innerHeight − visualViewport bottom).
 * - Every button preventDefaults pointerdown/mousedown so tapping it never
 *   blurs the textarea (which would drop the keyboard AND unmount this bar).
 *   The action lands on the line the caret was on.
 *
 * Mounted once by MobileBoard, so it never appears on desktop.
 */

const keepFocus = (e) => e.preventDefault();

// Ride just above the on-screen keyboard. Returns the keyboard height (0 when
// closed / unsupported), recomputed as the visual viewport resizes.
function useKeyboardOffset() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    // Walter 2026-08-21, second attempt. The bar kept hanging mid-screen after
    // a rotation, and my first fix (extra listeners + a re-measure once the
    // rotation settled) did NOT help — because the premise was wrong. It
    // computed the keyboard height as `window.innerHeight - vv.height`, and on
    // iOS `innerHeight` is simply not trustworthy across an orientation
    // change: it can stay stale indefinitely, so the subtraction keeps
    // yielding a large bogus "keyboard height" with no keyboard up, and the
    // bar parks itself in the middle of the screen. The empty space he saw
    // INSIDE the bar was its safe-area padding, which only makes sense when
    // the bar is actually at the bottom — same wrong offset, second symptom.
    //
    // So drop innerHeight entirely and self-calibrate: remember the TALLEST
    // visual viewport seen in the current orientation — that is the
    // keyboard-closed height, whatever the OS thinks — and treat the keyboard
    // as the difference from it. On rotation we reset the high-water mark, so
    // the first measurement in the new orientation defines "closed" and the
    // bar correctly hides.
    let maxH = vv.height;
    let settle = null;

    const update = () => {
      if (vv.height > maxH) maxH = vv.height;
      const kb = maxH - vv.height;
      setOffset(kb > 40 ? kb : 0); // ignore tiny deltas (browser chrome, not keyboard)
    };
    const scheduleSettle = () => {
      if (settle) clearTimeout(settle);
      settle = setTimeout(update, 350);
    };
    const onViewport = () => { update(); scheduleSettle(); };
    const onRotate = () => {
      // New orientation ⇒ a new keyboard-closed height. Re-baseline rather
      // than carry the old one over, and re-measure a couple of times while
      // iOS finishes the rotation animation.
      maxH = 0;
      setOffset(0);
      update();
      scheduleSettle();
      setTimeout(() => { maxH = 0; update(); }, 800);
    };

    update();
    vv.addEventListener('resize', onViewport);
    vv.addEventListener('scroll', onViewport);
    window.addEventListener('orientationchange', onRotate);
    window.addEventListener('resize', onViewport);
    return () => {
      if (settle) clearTimeout(settle);
      vv.removeEventListener('resize', onViewport);
      vv.removeEventListener('scroll', onViewport);
      window.removeEventListener('orientationchange', onRotate);
      window.removeEventListener('resize', onViewport);
    };
  }, []);
  return offset;
}

// Touch-sized format button. `swatchBg` fills it with a highlight colour.
function BarBtn({ title, active, swatchBg, onFire, children, bigGlyph }) {
  return (
    <button
      type="button" title={title} aria-label={title}
      onPointerDown={keepFocus} onMouseDown={keepFocus}
      onClick={(e) => { e.preventDefault(); onFire(); }}
      style={{
        // Walter 2026-07-02: a touch smaller than the first cut.
        flex: 'none', minWidth: 32, height: 30, padding: '0 7px',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-bright)'}`,
        borderRadius: 6,
        background: swatchBg || (active ? 'var(--accent-wash)' : 'var(--surface2)'),
        color: active && !swatchBg ? 'var(--accent)' : 'var(--text)',
        fontWeight: active ? 700 : 500,
        fontFamily: 'var(--mono)',
        fontSize: `calc(${bigGlyph ? 13 : 11}px * var(--ui-scale, 1))`,
        lineHeight: 1, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >{children}</button>
  );
}

function Sep() {
  return <div style={{ flex: 'none', width: 1, alignSelf: 'stretch', margin: '5px 2px', background: 'var(--border)' }} />;
}

const HL = [
  { idx: 1, label: 'green highlight', varName: '--hl-green' },
  { idx: 2, label: 'blue highlight', varName: '--hl-blue' },
  { idx: 3, label: 'red highlight', varName: '--hl-red' },
];

export default function MobileFormatBar() {
  const { focused, line, target } = useFormatTarget();
  const offset = useKeyboardOffset();

  // Only while a WRITABLE list textarea is focused AND the keyboard is actually
  // up. Folding the keyboard (its own chevron) often does NOT blur the
  // textarea, so `focused` alone left the bar stranded at the bottom (Walter
  // 2026-07-02, s3). The VisualViewport shrink is the real "keyboard is up"
  // signal — when it folds, offset drops to 0 and the bar hides.
  if (!focused || target?.readOnly || offset <= 0) return null;

  return (
    <div
      // Don't let a tap on the bar background steal focus from the textarea.
      onPointerDown={keepFocus} onMouseDown={keepFocus}
      // MarkdownEditor's caret-above-keyboard scroll reads this bar's top edge
      // so the caret clears the BAR, not just the keyboard (Walter 2026-07-19).
      data-mobile-format-bar="1"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: offset,
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '5px 8px calc(5px + env(safe-area-inset-bottom, 0px))',
        background: 'var(--surface)', borderTop: '1px solid var(--border-bright)',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        zIndex: 1200,
      }}
    >
      {/* Walter 2026-08-22: MOVE FIRST. "I use the up down arrows more than
          other formatting buttons" — and on a phone the bar scrolls, so what
          he reaches for most was the one thing off the right-hand edge.
          Otherwise the desktop-strip grouping is unchanged:
          move · emphasis · highlights · headings · checkboxes · special lines. */}
      <BarBtn title="move line up" onFire={() => formatActions.moveLine('up')}><ArrowIcon dir="up" size={13} /></BarBtn>
      <BarBtn title="move line down" onFire={() => formatActions.moveLine('down')}><ArrowIcon dir="down" size={13} /></BarBtn>
      <Sep />
      <BarBtn title="bold" onFire={() => formatActions.bold()}><BoldGlyph /></BarBtn>
      <BarBtn title="italic" onFire={() => formatActions.italic()}><ItalicGlyph /></BarBtn>
      <BarBtn title="strikethrough" onFire={() => formatActions.strike()}><StrikeGlyph /></BarBtn>
      <Sep />
      {HL.map((h) => (
        <BarBtn key={h.idx} title={h.label} active={line.color === h.idx}
          swatchBg={`var(${h.varName})`} onFire={() => formatActions.highlight(h.idx)}>{' '}</BarBtn>
      ))}
      <Sep />
      {[1, 2, 3].map((lvl) => (
        <BarBtn key={lvl} title={`heading level ${lvl}`} active={line.headerLevel === lvl}
          onFire={() => formatActions.header(lvl)}><HeaderGlyph level={lvl} /></BarBtn>
      ))}
      <Sep />
      <BarBtn title="checkbox" active={line.checkbox} onFire={() => formatActions.checkbox()}><CheckboxEmptyIcon size={14} /></BarBtn>
      <BarBtn title="mark as done" active={line.done} onFire={() => formatActions.done()}><CheckboxDoneIcon size={14} /></BarBtn>
      <Sep />
      <BarBtn title="insert a reminder on this line" active={line.bell} onFire={() => formatActions.reminder()}><BellIcon size={14} /></BarBtn>
      <BarBtn title="log this line (date-stamp)" active={line.log} onFire={() => formatActions.log()} bigGlyph><LogGlyph /></BarBtn>
    </div>
  );
}
